import { API_BASE_URL } from '../constants/api';

// Build a query string, skipping null/undefined/'' so callers can pass
// optional filters without sending them as the literal "undefined".
const buildQueryString = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    sp.append(k, v);
  });
  return sp.toString();
};

// Simple API client for making HTTP requests
class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      credentials: 'include', // Send HttpOnly auth cookie
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      let response = await fetch(url, config);
      
      // On 401, attempt a silent token refresh and retry once
      if (response.status === 401 && !endpoint.includes('/auth/')) {
        try {
          const { AUTH_ENDPOINTS } = await import('../constants/api');
          await fetch(AUTH_ENDPOINTS.REFRESH, { method: 'POST', credentials: 'include' });
          response = await fetch(url, config); // retry original request
        } catch (_) {
          // Refresh failed — clear auth state and redirect
          localStorage.removeItem('bhaAuthToken');
          window.location.href = '/login';
          throw new Error('Session expired');
        }
      }

      const contentType = response.headers.get('content-type');
      let responseData;
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        // If still 401 after refresh attempt, redirect to login
        if (response.status === 401) {
          localStorage.removeItem('bhaAuthToken');
          window.location.href = '/login';
        }
        // For error responses, return the response data instead of throwing
        // This allows the calling code to handle error messages properly
        return responseData;
      }

      return responseData;
    } catch (error) {
      // Network / CORS errors surface as TypeErrors ('Failed to fetch').
      // These are NOT authentication failures — don't redirect to login.
      if (error instanceof TypeError) {
        console.warn('Network error (possible CORS or connectivity issue):', error.message);
      } else {
        console.error('API request failed:', error);
      }
      throw error;
    }
  }

  async get(endpoint, params = {}) {
    const queryString = buildQueryString(params);
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  async getBlob(endpoint, params = {}) {
    const queryString = buildQueryString(params);
    const urlPath = queryString ? `${endpoint}?${queryString}` : endpoint;
    const url = `${this.baseURL}${urlPath}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // Send HttpOnly auth cookie
    });
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return { data: blob };
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

const api = new ApiClient();
export default api;
